import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import { FeatherDownload, FeatherX } from "@subframe/core";
import { FeatherShieldCheck } from "@subframe/core";
import { Button } from "../../../../ui/components/Button";
import { IconButton } from "../../../../ui/components/IconButton";
import { Loader } from "../../../../ui/components/Loader";
import { TextField } from "../../../../ui/components/TextField";
import { DialogLayout } from "../../../../ui/layouts/DialogLayout";
import { DateRangeFilter } from "../../../../shared/presentation/components/DateRangeFilter";
import {
  DateRangeValue,
  formatDateForApi,
} from "../../../../shared/presentation/utils/dateRangeFilters";
import { usePersistedDateRange } from "../../../../shared/presentation/hooks/usePersistedDateRange";
import { useMinimumLoading } from "../../../../shared/presentation/hooks/useMinimumLoading";
import { usePersistedSort } from "../../../../shared/presentation/hooks/usePersistedSort";
import { usePersistedState } from "../../../../shared/presentation/hooks/usePersistedState";
import {
  EXPORT_MAX_DATE_RANGE_INCLUSIVE_DAYS,
  addDaysLocal,
  parseYmdToLocalDate,
  toYmd,
  validateExportDateRange,
} from "../../../../shared/utils/date";
import AgentReviewDrawer from "../../../agent-review/presentation/components/AgentReviewDrawer";
import { AgentReviewListItem } from "../../../agent-review/domain/entities/AgentReviewEntities";
import { isZohoBackend } from "../../../../config/accounting";
import { BillEntity, BillListStatus } from "../../domain";
import { useBillCounts } from "../hooks/useBillCounts";
import { useBillExcelExport } from "../hooks/useBillExcelExport";
import { useBillList } from "../hooks/useBillList";
import { BillListToolbar } from "./BillListToolbar";
import { BillStatsCards } from "./BillStatsCards";
import { BillTable } from "./BillTable";

const ITEMS_PER_PAGE = 10;
const BILL_LIST_STATUSES: BillListStatus[] = [
  "all",
  "pending",
  "on_hold",
  "under_review",
  "posted",
  "duplicate",
  "rejected",
];
type ListLoadingReason = "initial" | "pagination" | "refresh" | null;

const deserializeBillStatus = (raw: string, fallback: BillListStatus) => {
  const parsed = JSON.parse(raw);
  return typeof parsed === "string" &&
    BILL_LIST_STATUSES.includes(parsed as BillListStatus)
    ? (parsed as BillListStatus)
    : fallback;
};

/**
 * The drawer speaks the queue page's row shape, so the dashboard's row is
 * translated rather than the drawer being taught a second one. The fields it
 * only displays may be null; the ones that change what it renders — status,
 * stale, pdf_available, is_posted — all come from the row.
 *
 * `is_posted` is always false: a posted bill is out of the agent's scope on
 * this page, so `agent_review` is null for it and the drawer never opens.
 */
const toAgentReviewItem = (
  bill: BillEntity,
  review: NonNullable<BillEntity["agent_review"]>,
): AgentReviewListItem => ({
  bill_id: Number(bill.id),
  jobid: bill.jobid ?? null,
  bill_number: bill.bill_json?.bill_number ?? null,
  vendor_id: bill.bill_json?.vendor_id ?? null,
  vendor_name: bill.bill_json?.vendor_name ?? null,
  date: bill.date ?? bill.bill_json?.date ?? null,
  total: bill.bill_json?.total != null ? String(bill.bill_json.total) : null,
  // `no_vendor` never reaches here — handleReviewClick only opens the drawer
  // for the three finished statuses.
  status: review.status as AgentReviewListItem["status"],
  run_id: review.run_id,
  // The drawer refetches the run itself; these are only the header's first
  // paint, so nothing is invented to fill them.
  verdict: null,
  summary: null,
  error: null,
  blocking_failures: [],
  pdf_available: review.pdf_available,
  pdf_error: review.pdf_error,
  is_posted: false,
  trigger_type: null,
  mismatch_count: review.mismatch_count,
  advisory_count: review.advisory_count,
  checked_at: review.checked_at,
  stale: review.stale,
});

const BillsSection: React.FC = () => {
  const navigate = useNavigate();
  const [status, setStatus] = usePersistedState<BillListStatus>(
    "bills.list.status",
    "all",
    { deserialize: deserializeBillStatus },
  );
  const [page, setPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [dateRange, setDateRange] = usePersistedDateRange(
    "bills.list.dateRange",
  );
  const [sort, setSort] = usePersistedSort("bills.list", {
    by: "created_at",
    order: "desc",
    label: "Created Date: Newest First",
  });
  const [listLoadingReason, setListLoadingReason] =
    useState<ListLoadingReason>("initial");
  const listLoadingStartedRef = useRef(false);

  const { bills, pagination, loading, error, fetchBills } = useBillList();
  const countParams = useMemo(
    () => ({
      date_from:
        dateRange.preset === "all_time"
          ? undefined
          : formatDateForApi(dateRange.from),
      date_to:
        dateRange.preset === "all_time"
          ? undefined
          : formatDateForApi(dateRange.to),
    }),
    [dateRange.from, dateRange.preset, dateRange.to],
  );
  const {
    counts: billCounts,
    loading: countsLoading,
    fetchCounts,
  } = useBillCounts(countParams);

  // ZOUR-67 (change 7) — the bill whose agent review is open in the drawer.
  const [agentReviewBill, setAgentReviewBill] =
    useState<AgentReviewListItem | null>(null);

  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [exportFromDate, setExportFromDate] = useState<string>("");
  const [exportToDate, setExportToDate] = useState<string>("");
  const [exportError, setExportError] = useState<string>("");
  const { download: downloadExport, loading: isExporting } =
    useBillExcelExport();

  const listParams = useMemo(
    () => ({
      page,
      limit: ITEMS_PER_PAGE,
      status,
      search: searchTerm,
      dateFrom:
        dateRange.preset === "all_time"
          ? undefined
          : formatDateForApi(dateRange.from),
      dateTo:
        dateRange.preset === "all_time"
          ? undefined
          : formatDateForApi(dateRange.to),
      sortBy: sort.by as
        | "created_at"
        | "updated_at"
        | "filename"
        | "status"
        | "original_filename",
      sortOrder: sort.order as "asc" | "desc",
    }),
    [
      dateRange.from,
      dateRange.preset,
      dateRange.to,
      page,
      searchTerm,
      sort.by,
      sort.order,
      status,
    ],
  );

  useEffect(() => {
    const timeoutId = window.setTimeout(
      () => {
        fetchBills(listParams);
      },
      searchTerm.trim() ? 500 : 0,
    );

    return () => window.clearTimeout(timeoutId);
  }, [fetchBills, listParams, searchTerm]);

  useEffect(() => {
    if (loading) {
      listLoadingStartedRef.current = true;
      return;
    }

    if (listLoadingStartedRef.current) {
      listLoadingStartedRef.current = false;
      setListLoadingReason(null);
    }
  }, [loading]);

  const resetExportState = () => {
    setExportFromDate("");
    setExportToDate("");
    setExportError("");
  };

  const exportDateRangeError = validateExportDateRange(
    exportFromDate,
    exportToDate,
  );

  const handleStatusChange = (nextStatus: BillListStatus) => {
    if (nextStatus === status) return;

    setListLoadingReason("refresh");
    setStatus(nextStatus);
    setPage(1);
  };

  const handleDateRangeChange = (nextDateRange: DateRangeValue) => {
    if (
      nextDateRange.preset === dateRange.preset &&
      nextDateRange.from?.getTime() === dateRange.from?.getTime() &&
      nextDateRange.to?.getTime() === dateRange.to?.getTime()
    ) {
      return;
    }

    setListLoadingReason("refresh");
    setDateRange(nextDateRange);
    setPage(1);
  };

  const handleSearchTermChange = (value: string) => {
    if (value === searchTerm) return;

    setListLoadingReason("refresh");
    setSearchTerm(value);
    setPage(1);
  };

  const handleSortChange = (
    sortBy: string,
    sortOrder: string,
    label: string,
  ) => {
    if (
      sortBy === sort.by &&
      sortOrder === sort.order &&
      label === sort.label
    ) {
      return;
    }

    setListLoadingReason("refresh");
    setSort({ by: sortBy, order: sortOrder, label });
    setPage(1);
  };

  const handleRefresh = useCallback(() => {
    setListLoadingReason("refresh");
    fetchBills(listParams);
    fetchCounts(countParams);
  }, [countParams, fetchBills, fetchCounts, listParams]);

  const handlePageChange = (nextPage: number) => {
    if (nextPage === page) return;

    setListLoadingReason("pagination");
    setPage(nextPage);
  };

  /**
   * ZOUR-67 (change 7) — Review either opens the agent's drawer or goes
   * straight to the review page.
   *
   * The button is never hidden or disabled by the agent. That was the original
   * idea and it was dropped deliberately: 18 of the 67 pending bills here have
   * no finished run (11 with no vendor, 2 emailed, 5 manual), and an edited
   * bill flips to Checking for up to a cron interval — so gating on the agent
   * would take the Review button away from a quarter of the page for reasons
   * that have nothing to do with the accountant's work.
   *
   * The drawer only appears when there is something to show: a finished run.
   * A stale one still counts — it says so in its own banner, and an out-of-date
   * reading of a bill is worth more than no reading at all.
   */
  const handleReviewClick = (document: BillEntity) => {
    const review = document.agent_review;
    const hasFinishedRun =
      review?.run_id != null &&
      ["ready", "needs_attention", "not_checked"].includes(review.status);

    if (hasFinishedRun) {
      setAgentReviewBill(toAgentReviewItem(document, review!));
      return;
    }

    openReviewPage(document);
  };

  const openReviewPage = (document: BillEntity) => {
    navigate(`/bills/review`, {
      state: {
        billJson: document.bill_json,
        billDocument: document,
        fileName: document.original_filename,
        documentId: document.id,
        signedUrl: document.signed_url,
        isPartyMatched:
          document.bill_json.vendor_id !== undefined &&
          document.bill_json.vendor_id !== "",
        isIdMatched: document.bill_json.bill_number !== "",
        isEdit: document.status === "1",
        jobId: document.jobid,
      },
    });
  };

  const handleExportFromDateChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const v = e.target.value;
    setExportFromDate(v);
    setExportError("");
    if (!v) return;
    const fromD = parseYmdToLocalDate(v);
    if (!fromD) return;
    if (exportToDate) {
      const toD = parseYmdToLocalDate(exportToDate);
      const maxTo = addDaysLocal(
        fromD,
        EXPORT_MAX_DATE_RANGE_INCLUSIVE_DAYS - 1,
      );
      if (toD && toD > maxTo) {
        setExportToDate(toYmd(maxTo));
      } else if (toD && toD < fromD) {
        setExportToDate(v);
      }
    }
  };

  const handleExportToDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = e.target.value;
    setExportToDate(v);
    setExportError("");
    if (!v) return;
    const toD = parseYmdToLocalDate(v);
    if (!toD) return;
    if (exportFromDate) {
      const fromD = parseYmdToLocalDate(exportFromDate);
      const minFrom = addDaysLocal(
        toD,
        -(EXPORT_MAX_DATE_RANGE_INCLUSIVE_DAYS - 1),
      );
      if (fromD && fromD < minFrom) {
        setExportFromDate(toYmd(minFrom));
      } else if (fromD && fromD > toD) {
        setExportFromDate(v);
      }
    }
  };

  const exportFromMin =
    exportToDate.trim() && parseYmdToLocalDate(exportToDate.trim())
      ? toYmd(
          addDaysLocal(
            parseYmdToLocalDate(exportToDate.trim())!,
            -(EXPORT_MAX_DATE_RANGE_INCLUSIVE_DAYS - 1),
          ),
        )
      : undefined;
  const exportFromMax =
    exportToDate.trim() && parseYmdToLocalDate(exportToDate.trim())
      ? exportToDate.trim()
      : undefined;
  const exportToMin =
    exportFromDate.trim() && parseYmdToLocalDate(exportFromDate.trim())
      ? exportFromDate.trim()
      : undefined;
  const exportToMax =
    exportFromDate.trim() && parseYmdToLocalDate(exportFromDate.trim())
      ? toYmd(
          addDaysLocal(
            parseYmdToLocalDate(exportFromDate.trim())!,
            EXPORT_MAX_DATE_RANGE_INCLUSIVE_DAYS - 1,
          ),
        )
      : undefined;

  const handleExportDownload = async () => {
    setExportError("");
    const rangeErr = validateExportDateRange(exportFromDate, exportToDate);
    if (rangeErr) {
      setExportError(rangeErr);
      return;
    }

    try {
      await downloadExport({
        fromDate: exportFromDate.trim(),
        toDate: exportToDate.trim(),
      });
      setIsExportModalOpen(false);
      resetExportState();
    } catch (e: any) {
      setExportError(e?.message || "Failed to download bills export.");
    }
  };

  const showTableSkeleton =
    (listLoadingReason === "initial" && !pagination) ||
    listLoadingReason === "pagination" ||
    (loading && !pagination);
  const showInlineLoading =
    !showTableSkeleton && (listLoadingReason === "refresh" || loading);
  const smoothTableSkeleton = useMinimumLoading(showTableSkeleton);
  const smoothInlineLoading = useMinimumLoading(showInlineLoading);

  return (
    <div className="flex w-full grow shrink-0 basis-0 flex-col items-start gap-8 bg-default-background px-8 pt-8 pb-40">
      <div className="flex w-full items-center justify-between">
        <div className="flex flex-col items-start gap-2">
          <span className="text-heading-2 font-heading-2 text-default-font">
            Bills
          </span>
          <span className="text-body font-body text-subtext-color">
            Track and manage your bill workflow
          </span>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="neutral-tertiary"
            icon={<FeatherDownload />}
            onClick={() => setIsExportModalOpen(true)}
          >
            Export
          </Button>
          {/* ZOUR-67 — Zoho-only for now while Agent Review is still proven in prod. */}
          {isZohoBackend() && (
            <Button
              variant="neutral-secondary"
              icon={<FeatherShieldCheck />}
              onClick={() => navigate("/bills/agent-review")}
            >
              Agent Review
            </Button>
          )}
          <Button onClick={() => navigate("/bills/all")}>View All Bills</Button>
        </div>
      </div>

      <DateRangeFilter value={dateRange} onChange={handleDateRangeChange} />

      <BillStatsCards
        counts={billCounts}
        loading={countsLoading}
        onSelectStatus={handleStatusChange}
      />

      <BillListToolbar
        status={status}
        onStatusChange={handleStatusChange}
        searchTerm={searchTerm}
        onSearchTermChange={handleSearchTermChange}
        sort={sort}
        onSortChange={handleSortChange}
        onRefresh={handleRefresh}
        loading={loading}
      />

      <BillTable
        bills={bills}
        loading={smoothTableSkeleton}
        inlineLoading={smoothInlineLoading}
        error={error}
        pagination={pagination}
        itemsPerPage={ITEMS_PER_PAGE}
        searchTerm={searchTerm}
        onReview={handleReviewClick}
        onPageChange={handlePageChange}
      />

      <AgentReviewDrawer
        bill={agentReviewBill}
        canEdit={true}
        variant="dashboard"
        onClose={() => setAgentReviewBill(null)}
        onChanged={handleRefresh}
      />

      <DialogLayout
        open={isExportModalOpen}
        onOpenChange={(open) => {
          setIsExportModalOpen(open);
          if (!open) resetExportState();
        }}
      >
        <div className="flex w-144 flex-col items-start gap-6 bg-white px-8 py-8">
          <div className="flex w-full items-center justify-between">
            <span className="text-heading-2 font-heading-2 text-default-font">
              Export bills to Excel
            </span>
            <IconButton
              icon={<FeatherX />}
              onClick={() => setIsExportModalOpen(false)}
              disabled={isExporting}
            />
          </div>

          <div className="flex w-full flex-col items-start gap-4">
            {exportError && (
              <div className="w-full rounded-md border border-error-200 bg-error-50 px-4 py-3">
                <span className="text-body font-body text-error-700">
                  {exportError}
                </span>
              </div>
            )}

            <div className="flex w-full items-start gap-4">
              <TextField
                className="h-auto w-full"
                variant="filled"
                label="From date"
                helpText="Required"
              >
                <TextField.Input
                  type="date"
                  required
                  value={exportFromDate}
                  onChange={handleExportFromDateChange}
                  disabled={isExporting}
                  min={exportFromMin}
                  max={exportFromMax}
                />
              </TextField>
              <TextField
                className="h-auto w-full"
                variant="filled"
                label="To date"
                helpText="Required"
              >
                <TextField.Input
                  type="date"
                  required
                  value={exportToDate}
                  onChange={handleExportToDateChange}
                  disabled={isExporting}
                  min={exportToMin}
                  max={exportToMax}
                />
              </TextField>
            </div>

            <div className="w-full rounded-md border border-solid border-neutral-border bg-neutral-50 px-4 py-3">
              <span className="text-body font-body text-subtext-color">
                Select a date range of up to{" "}
                {EXPORT_MAX_DATE_RANGE_INCLUSIVE_DAYS} days (inclusive). Both
                dates are required.
              </span>
            </div>
          </div>

          <div className="flex w-full items-center justify-end gap-2">
            <Button
              variant="neutral-tertiary"
              onClick={() => setIsExportModalOpen(false)}
              disabled={isExporting}
            >
              Cancel
            </Button>
            <Button
              onClick={handleExportDownload}
              disabled={isExporting || !!exportDateRangeError}
            >
              {isExporting ? (
                <div className="flex items-center gap-2">
                  <Loader size="small" />
                  <span>Generating...</span>
                </div>
              ) : (
                "Download"
              )}
            </Button>
          </div>
        </div>
      </DialogLayout>
    </div>
  );
};

export default BillsSection;
